import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { SubscriptionStatus, SubscriptionTier, DailyUsage, TIER_LIMITS, UsageLimits } from '../types';
import { useAuth } from './AuthContext';
import { fetchOwnSubscription } from '../services/subscriptionService';
import { normalizeTier } from '../types/tiers';

interface SubscriptionContextType {
  subscription: SubscriptionStatus;
  limits: UsageLimits;
  dailyUsage: DailyUsage;
  incrementUsage: () => void;
  canRedact: () => boolean;
  getRemainingRedactions: () => number;
  /** Optimistic UI only — does not write Supabase. Cleared on next trusted sync. */
  applyOptimisticSubscription: (tier: SubscriptionTier, expiresAt: number | null) => void;
  markSubscriptionCancelled: (currentPeriodEnd?: number | null) => void;
  checkSubscriptionStatus: () => void;
  syncSubscriptionFromDB: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

const STORAGE_KEYS = {
  SUBSCRIPTION: 'redact_ai_subscription',
  DAILY_USAGE: 'redact_ai_daily_usage',
};

const getTodayDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

const FREE_STATUS: SubscriptionStatus = {
  tier: 'free',
  expiresAt: null,
  isActive: false,
  cancelAtPeriodEnd: false,
  status: 'none',
};

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();

  const [subscription, setSubscription] = useState<SubscriptionStatus>(FREE_STATUS);

  const [dailyUsage, setDailyUsage] = useState<DailyUsage>({
    count: 0,
    date: getTodayDate(),
  });

  const syncSubscriptionFromDB = useCallback(async () => {
    if (!user) {
      setSubscription(FREE_STATUS);
      return;
    }

    try {
      const trusted = await fetchOwnSubscription(user.id);
      if (!trusted) {
        setSubscription(FREE_STATUS);
        localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION, JSON.stringify(FREE_STATUS));
        return;
      }

      const next: SubscriptionStatus = {
        tier: trusted.tier,
        expiresAt: trusted.expiresAt,
        isActive: trusted.isActive,
        cancelAtPeriodEnd: trusted.cancelAtPeriodEnd,
        status: trusted.status,
      };
      setSubscription(next);
      localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION, JSON.stringify(next));
    } catch (err) {
      console.error('Error syncing subscription:', err);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      syncSubscriptionFromDB();
    } else {
      const stored = localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as SubscriptionStatus;
          setSubscription({
            ...parsed,
            tier: normalizeTier(parsed.tier),
          });
        } catch {
          setSubscription(FREE_STATUS);
        }
      } else {
        setSubscription(FREE_STATUS);
      }
    }
  }, [user, syncSubscriptionFromDB]);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEYS.DAILY_USAGE);
    const today = getTodayDate();

    if (stored) {
      const parsed: DailyUsage = JSON.parse(stored);
      if (parsed.date !== today) {
        const newUsage = { count: 0, date: today };
        setDailyUsage(newUsage);
        localStorage.setItem(STORAGE_KEYS.DAILY_USAGE, JSON.stringify(newUsage));
      } else {
        setDailyUsage(parsed);
      }
    } else {
      const newUsage = { count: 0, date: today };
      localStorage.setItem(STORAGE_KEYS.DAILY_USAGE, JSON.stringify(newUsage));
    }
  }, []);

  const checkSubscriptionStatus = useCallback(() => {
    if (user) {
      void syncSubscriptionFromDB();
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEYS.SUBSCRIPTION);
    if (stored) {
      const parsed: SubscriptionStatus = JSON.parse(stored);
      const now = Date.now();
      if (parsed.expiresAt && parsed.expiresAt > now) {
        setSubscription({ ...parsed, tier: normalizeTier(parsed.tier), isActive: true });
      } else if (parsed.tier !== 'free') {
        setSubscription(FREE_STATUS);
        localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION, JSON.stringify(FREE_STATUS));
      }
    }
  }, [user, syncSubscriptionFromDB]);

  const incrementUsage = useCallback(() => {
    const today = getTodayDate();
    const newUsage: DailyUsage = {
      count: dailyUsage.date === today ? dailyUsage.count + 1 : 1,
      date: today,
    };
    setDailyUsage(newUsage);
    localStorage.setItem(STORAGE_KEYS.DAILY_USAGE, JSON.stringify(newUsage));
  }, [dailyUsage]);

  const limits = TIER_LIMITS[subscription.tier] ?? TIER_LIMITS.free;

  const canRedact = useCallback((): boolean => {
    if (subscription.isActive && subscription.tier !== 'free') {
      return true;
    }
    return dailyUsage.count < limits.maxRedactionsPerDay;
  }, [subscription, dailyUsage, limits]);

  const getRemainingRedactions = useCallback((): number => {
    if (subscription.isActive && subscription.tier !== 'free') {
      return Infinity;
    }
    return Math.max(0, limits.maxRedactionsPerDay - dailyUsage.count);
  }, [subscription, dailyUsage, limits]);

  const applyOptimisticSubscription = useCallback(
    (tier: SubscriptionTier, expiresAt: number | null) => {
      const next: SubscriptionStatus = {
        tier: normalizeTier(tier),
        expiresAt,
        isActive: normalizeTier(tier) !== 'free',
        cancelAtPeriodEnd: false,
      };
      setSubscription(next);
      // Do not persist optimistic paid state to localStorage as source of truth
    },
    []
  );

  const markSubscriptionCancelled = useCallback(
    (currentPeriodEnd?: number | null) => {
      const expiresAt = currentPeriodEnd ?? subscription.expiresAt;
      const updated: SubscriptionStatus = {
        ...subscription,
        expiresAt,
        cancelAtPeriodEnd: true,
        isActive: true,
      };
      setSubscription(updated);
      localStorage.setItem(STORAGE_KEYS.SUBSCRIPTION, JSON.stringify(updated));
      if (user) {
        void syncSubscriptionFromDB();
      }
    },
    [subscription, user, syncSubscriptionFromDB]
  );

  return (
    <SubscriptionContext.Provider
      value={{
        subscription,
        limits,
        dailyUsage,
        incrementUsage,
        canRedact,
        getRemainingRedactions,
        applyOptimisticSubscription,
        markSubscriptionCancelled,
        checkSubscriptionStatus,
        syncSubscriptionFromDB,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};
